import {
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Input,
  Renderer2,
  ViewContainerRef,
} from '@angular/core';
import {UiTooltipComponent} from '../shared/ui-kits/ui-tooltip/ui-tooltip.component';
import {TooltipPosition} from '../enums/tooltip-position';

interface ItooltipStyles {
  top: number;
  left: number;
}

@Directive({
  selector: '[uiTooltip]',
  standalone: true,
})
export class UiTooltipDirective {
  @Input() tTooltip: string = '';
  @Input() tooltipPosition: TooltipPosition = TooltipPosition.Right;
  @Input() tooltipClass: string = '';
  @Input() tooltipDisabled: boolean = false;
  private tooltipComponentRef: ComponentRef<UiTooltipComponent> | null = null;
  constructor(
    private el: ElementRef,
    private viewContainer: ViewContainerRef,
    private render: Renderer2
  ) {}

  @HostListener('mouseenter') onMouseEnter() {
    if (!this.tooltipComponentRef && !this.tooltipDisabled) {
      this.createTooltip();
      this.setPosition();
    }
  }

  @HostListener('mouseleave') onMouseLeave() {
    if (this.tooltipComponentRef) this.destroyTooltip();
  }

  private createTooltip() {
    this.tooltipComponentRef =
      this.viewContainer.createComponent(UiTooltipComponent);
    this.tooltipComponentRef.setInput('tTooltip', this.tTooltip);
    this.tooltipComponentRef.setInput('isShow', true);
  }

  private destroyTooltip() {
    this.tooltipComponentRef?.setInput('isShow', false);
    this.tooltipComponentRef?.destroy();
    this.tooltipComponentRef = null;
  }

  private setPosition() {
    const currentEl = this.el.nativeElement;
    const tooltipEl =
      this.tooltipComponentRef?.location.nativeElement.querySelector(
        '.tooltip-content'
      );
    const hostPos = currentEl.getBoundingClientRect();
    const relativeParent = this.findRelativeParent(currentEl.parentElement);
    const relativePos = relativeParent
      ? relativeParent.getBoundingClientRect()
      : {top: 0, left: 0};

    if (tooltipEl) {
      if (this.tooltipClass) this.render.addClass(tooltipEl, this.tooltipClass);
      this.tooltipComponentRef?.changeDetectorRef.detectChanges();
      const tooltipPos = tooltipEl.getBoundingClientRect();
      const positionStyles = this.calculatePosition(
        hostPos,
        tooltipPos,
        relativePos
      );

      this.applyTooltipStyles(positionStyles);
    }
  }

  private calculatePosition(
    hostPos: DOMRect,
    tooltipPos: DOMRect,
    relativePos: ItooltipStyles
  ): ItooltipStyles {
    const styles: ItooltipStyles = {
      top: hostPos.top - relativePos.top + window.scrollY,
      left: hostPos.left - relativePos.left + window.scrollX,
    };

    switch (this.tooltipPosition) {
      case TooltipPosition.Top:
        styles.top -= tooltipPos.height - 10;
        styles.left += (hostPos.width - tooltipPos.width) / 2;
        break;

      case TooltipPosition.Bottom:
        styles.top += hostPos.height + 5;
        styles.left += (hostPos.width - tooltipPos.width) / 2;
        break;

      default:
        styles.top += (hostPos.height - tooltipPos.height) / 2;
        styles.left += hostPos.width + 10;
        break;
    }

    return styles;
  }

  private applyTooltipStyles(positionStyles: ItooltipStyles) {
    const tooltipEl =
      this.tooltipComponentRef?.location.nativeElement.querySelector(
        '.tooltip-content'
      );

    this.render.setStyle(tooltipEl, 'top', `${positionStyles.top}px`);
    this.render.setStyle(tooltipEl, 'left', `${positionStyles.left}px`);
  }

  findRelativeParent(
    parent: Element | undefined | null
  ): Element | undefined | null {
    if (parent && getComputedStyle(parent).position === 'static') {
      return this.findRelativeParent(parent.parentElement);
    }
    return parent;
  }
}
